<template>
  <Teleport to="body">
    <Modal v-model="show">
      <ModalContent size="medium">
        <template #header>
          <Heading level="h2">{{ t('networking.wifi_modal_title') }}</Heading>
        </template>

        <form class="tw-space-y-5" @submit.prevent="connect">
          <div class="tw-grid tw-grid-cols-12 tw-gap-5">
            <FormField
              class="tw-col-span-full"
              size="small"
              v-model="ssid"
              :label="t('networking.wifi_ssid')"
              :field-attrs="{ class: 'tw-font-mono', autocomplete: 'off' }"
            />

            <!-- No band: the GL repeater finds the network on either band by itself. -->
            <FormField
              class="tw-col-span-full"
              size="small"
              v-model="password"
              :label="t('networking.wifi_password')"
              :field-attrs="{ class: 'tw-font-mono', autocomplete: 'off' }"
            />
          </div>

          <div class="tw-space-y-2">
            <Text>{{
              t(enabled ? 'networking.wifi_modal_note' : 'networking.wifi_modal_note_enable', { priority })
            }}</Text>
            <Text>{{ t('networking.wifi_modal_revert_note', { count: timeoutSec }) }}</Text>
            <Text class="tw-font-black tw-text-orange">{{ t('networking.wifi_stream_drop_warning') }}</Text>
          </div>
        </form>

        <template #footer="{ reject }">
          <Button color="transparent" outlined @click="reject">{{ t('networking.confirm_cancel') }}</Button>
          <Button color="semiTransparentWhite" outlined :disabled="!canConnect" @click="connect">
            {{ t('networking.wifi_modal_accept') }}
          </Button>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Button, FormField, Heading, Modal, ModalContent, Text } from '@slideslive/fuse-kit/vue';

const props = defineProps({
  // Current or last network and its key, prefilled when the modal opens. The key is shown on
  // purpose: operators read it from here.
  currentSsid: { type: String, default: '' },
  currentPassword: { type: String, default: '' },
  enabled: { type: Boolean, default: false },
  priority: { type: Number, default: 0 },
  timeoutSec: { type: Number, default: 60 },
});

const show = defineModel();

// connect({ ssid, password }); the parent sends it and shows the progress.
const emit = defineEmits(['connect']);

const { t } = useI18n();

const ssid = ref('');
const password = ref('');

watch(show, (open) => {
  if (!open) return;
  ssid.value = props.currentSsid || '';
  password.value = props.currentPassword || '';
});

// The prefilled key belongs to the current network: drop it when the operator types another SSID.
watch(ssid, (next) => {
  if (next.trim() !== props.currentSsid && password.value === props.currentPassword) password.value = '';
});

const canConnect = computed(() => ssid.value.trim().length > 0);

const connect = () => {
  if (!canConnect.value) return;
  emit('connect', { ssid: ssid.value.trim(), password: password.value.trim() });
  show.value = false;
};
</script>
